import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { websiteBuilderGenerateDefaultButton } from "../../../../../nodes/element/websiteBuilderButton";
import { websiteBuilderGenerateDefaultParagraph } from "../../../../../nodes/element/websiteBuilderParagraph";
import { websiteBuilderGenerateDefaultDivider } from "../../../../../nodes/element/websiteBuilderDivider";
import { websiteBuilderGenerateDefaultHeading } from "../../../../../nodes/element/websiteBuilderHeading";
import { websiteBuilderGenerateDefaultLink } from "../../../../../nodes/element/websiteBuilderLink";
import { websiteBuilderGenerateDefaultList } from "../../../../../nodes/element/websiteBuilderList";
import { websiteBuilderGenerateDefaultLogo } from "../../../../../nodes/element/websiteBuilderLogo";
import { websiteBuilderGenerateDefaultIcon } from "../../../../../nodes/element/websiteBuilderIcon";
import { useWebsiteBuilderElementMetadata } from "../../../../../hooks/elementMetadata/useWebsiteBuilderElementMetadata";
import { WebsiteBuilderToolbarMenuView } from "../../WebsiteBuilderToolbarMenuView";
import { DraggableNode } from "../../draggableNode/DraggableNode";
import { TWebsiteBuilderDraggableNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNode";
import { websiteBuilderGenerateDefaultNavigationMenu } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderNavigationMenu";
import { useWebsiteBuilderContainerMetadata } from "pages/secure/facility/websiteBuilder/editor/hooks/containerMetadata/useWebsiteBuilderContainerMetadata";
import { websiteBuilderGenerateDefaultFlex } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderFlex";
import { websiteBuilderGenerateDefaultGrid } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderGrid";
import { websiteBuilderGenerateDefaultForm } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderForm";
import { websiteBuilderGenerateDefaultTable } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTable";
import { websiteBuilderGenerateDefaultEmbed } from "../../../../../nodes/element/websiteBuilderEmbed";
import { websiteBuilderGenerateDefaultSlideshow } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderSlideshow";
import { websiteBuilderGenerateDefaultBox } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderBox";
import { websiteBuilderGenerateDefaultTabset } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTabset";
import { websiteBuilderGenerateDefaultMarquee } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderMarquee";

interface IAddElementsViewDraggableNode {
  icon: IconProp;
  title: string;
  item: TWebsiteBuilderDraggableNode;
}

export function AddElementsView() {
  const { t } = useTranslation();

  const elementMetadata = useWebsiteBuilderElementMetadata();
  const containerMetadata = useWebsiteBuilderContainerMetadata();
  
  const generalElements: IAddElementsViewDraggableNode[] = useMemo(
    () => [
      {
        ...elementMetadata.button,
        item: websiteBuilderGenerateDefaultButton(),
      },
      {
        ...elementMetadata.heading,
        item: websiteBuilderGenerateDefaultHeading(),
      },
      {
        ...elementMetadata.paragraph,
        item: websiteBuilderGenerateDefaultParagraph(),
      },
      {
        ...elementMetadata.list,
        item: websiteBuilderGenerateDefaultList(),
      },
      {
        ...elementMetadata.link,
        item: websiteBuilderGenerateDefaultLink(),
      },
      {
        ...elementMetadata.divider,
        item: websiteBuilderGenerateDefaultDivider(),
      },
      {
        ...elementMetadata.embed,
        item: websiteBuilderGenerateDefaultEmbed(),
      },
      {
        ...elementMetadata.logo,
        item: websiteBuilderGenerateDefaultLogo(),
      },
      {
        ...elementMetadata.icon,
        item: websiteBuilderGenerateDefaultIcon(),
      },
    ],
    [elementMetadata],
  );

  const navigationElements: IAddElementsViewDraggableNode[] = useMemo(
    () => [
      {
        ...elementMetadata["navigation-menu"],
        item: websiteBuilderGenerateDefaultNavigationMenu(),
      },
    ],
    [elementMetadata],
  );

  const generalContainers: IAddElementsViewDraggableNode[] = useMemo(
    () => [
      {
        ...containerMetadata.box,
        item: websiteBuilderGenerateDefaultBox(),
      },
      {
        ...containerMetadata.flex,
        item: websiteBuilderGenerateDefaultFlex(),
      },
      {
        ...containerMetadata.grid,
        item: websiteBuilderGenerateDefaultGrid(),
      },
      {
        ...containerMetadata.tabset,
        item: websiteBuilderGenerateDefaultTabset(),
      },
      {
        ...containerMetadata.slideshow,
        item: websiteBuilderGenerateDefaultSlideshow(),
      },
      {
        ...containerMetadata.form,
        item: websiteBuilderGenerateDefaultForm(),
      },
      {
        ...containerMetadata.table,
        item: websiteBuilderGenerateDefaultTable(),
      },
      {
        ...containerMetadata.marquee,
        item: websiteBuilderGenerateDefaultMarquee(),
      },
    ],
    [containerMetadata],
  );
  
  function renderGeneral() {
    return (
      <div className="builder-toolbar-palette-grid">
        {generalElements.map((element, index) => {
          return (
            <DraggableNode key={index} item={element.item}>
              <FontAwesomeIcon className="builder-toolbar-palette-item-icon" icon={element.icon} />
              <div className="builder-toolbar-palette-item-title">{element.title}</div>
            </DraggableNode>
          );
        })}
      </div>
    );
  }

  function renderNavigation() {
    return (
      <div className="builder-toolbar-palette-grid">
        {navigationElements.map((element, index) => {
          return (
            <DraggableNode key={index} item={element.item}>
              <FontAwesomeIcon className="builder-toolbar-palette-item-icon" icon={element.icon} />
              <div className="builder-toolbar-palette-item-title">{element.title}</div>
            </DraggableNode>
          );
        })}
      </div>
    );
  }

  function renderContainers() {
    return (
      <div className="builder-toolbar-palette-grid">
        {generalContainers.map((container, index) => {
          return (
            <DraggableNode key={index} item={container.item}>
              <FontAwesomeIcon className="builder-toolbar-palette-item-icon" icon={container.icon} />
              <div className="builder-toolbar-palette-item-title">{container.title}</div>
            </DraggableNode>
          );
        })}
      </div>
    );
  }

  return (
    <WebsiteBuilderToolbarMenuView
      title={t("Add elements")}
      sections={[
        {
          title: t("General"),
          content: renderGeneral(),
        },
        {
          title: t("Navigation"),
          content: renderNavigation(),
        },
        {
          title: t("Containers"),
          content: renderContainers(),
        },
      ]}
    />
  );
}
